// EPG / catch-up time helpers.
//
// Xtream EPG timestamps (start_timestamp/stop_timestamp) are unix epoch (UTC).
// Two distinct concerns must not be conflated:
//   • "Is it airing now?"  → compare raw unix vs Date.now() (no offset).
//   • "What clock to show / send for catch-up?" → the PROVIDER's wall clock,
//      which is UTC + the provider's timezone offset (epgOffsetMinutes).
//
// We render provider wall-clock by shifting the unix time by the offset and then
// reading it back with UTC getters — making display and catch-up independent of
// the viewer's browser timezone.

import type { XtreamServerInfo } from '@/types/xtream';

const clampOffset = (m: number) => Math.max(-720, Math.min(720, m));

/**
 * Derive the provider's timezone offset (minutes from UTC) from the Xtream auth
 * `server_info`, using its reported wall clock (`time_now`) vs unix (`timestamp_now`).
 * Returns null when the data is missing/unparseable. Rounded to the nearest 15
 * minutes to absorb clock skew while still supporting :30/:45 zones.
 */
export function serverTzOffsetMinutes(info?: XtreamServerInfo | null): number | null {
  if (!info) return null;
  const timeNow = info.time_now;
  const tsNow = Number(info.timestamp_now);
  if (!timeNow || !tsNow || isNaN(tsNow)) return null;
  // Interpret the server's local wall-clock string as if it were UTC.
  const iso = timeNow.trim().replace(' ', 'T') + 'Z';
  const localAsUtcMs = Date.parse(iso);
  if (isNaN(localAsUtcMs)) return null;
  const diffMin = (localAsUtcMs - tsNow * 1000) / 60000;
  if (!isFinite(diffMin)) return null;
  return clampOffset(Math.round(diffMin / 15) * 15);
}

const pad = (n: number) => String(n).padStart(2, '0');

/** A Date whose UTC getters read out the provider wall-clock for `unixSec`. */
export function providerDate(unixSec: number, offsetMinutes: number): Date {
  return new Date((unixSec + offsetMinutes * 60) * 1000);
}

/** Provider wall-clock time as "h:mm AM/PM". */
export function formatProviderTime(unixSec: number, offsetMinutes: number): string {
  const d = providerDate(unixSec, offsetMinutes);
  const h = d.getUTCHours();
  const m = d.getUTCMinutes();
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${pad(m)} ${ampm}`;
}

/** Provider wall-clock 24h "HH:MM" (compact, for dense grids). */
export function formatProviderHM(unixSec: number, offsetMinutes: number): string {
  const d = providerDate(unixSec, offsetMinutes);
  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

/**
 * Unix timestamp (seconds) of provider-local midnight for the day that is
 * `dayOffset` days from "now" in provider time. Used to lay out the grid in
 * provider-day space.
 */
export function providerDayStartUnix(dayOffset: number, offsetMinutes: number): number {
  const offsetSec = offsetMinutes * 60;
  const nowShifted = Math.floor(Date.now() / 1000) + offsetSec;
  const d = new Date(nowShifted * 1000);
  const midnightShifted = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / 1000;
  // midnightShifted is in shifted space; convert back to real unix and add days.
  return midnightShifted - offsetSec + dayOffset * 86400;
}

/** Provider-local calendar label for a day `dayOffset` days from today. */
export function formatProviderDayLabel(dayOffset: number, offsetMinutes: number): string {
  const startUnix = providerDayStartUnix(dayOffset, offsetMinutes);
  // Read the day in provider space.
  const d = providerDate(startUnix, offsetMinutes);
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}
